// @ts-nocheck
import { useState, useMemo, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Button,
  Group,
  Stack,
  Paper,
  Text,
  Modal,
  TextInput,
  Select,
  SimpleGrid,
  SegmentedControl,
  Box,
  ActionIcon,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconCalendar,
  IconTable,
} from '@tabler/icons-react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useDisclosure } from '@mantine/hooks';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { useScheduleWeek, useCreateShift } from '../../hooks/useSchedules';
import { ShiftCalendar, type ShiftData } from '../../components/ShiftCalendar';

interface Shift {
  id: string;
  employeeId: string;
  startTime: string;
  endTime: string;
  status: string;
  branchId: string;
  departmentId: string;
}

interface ScheduleWeekData {
  weekStart: string;
  weekEnd: string;
  shifts: Shift[];
}

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0] ?? '';
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SHIFT_STATUS_COLORS: Record<string, string> = {
  scheduled: 'blue',
  confirmed: 'green',
  in_progress: 'cyan',
  completed: 'gray',
  cancelled: 'red',
  no_show: 'orange',
};

export function ScheduleCalendarPage() {
  const [viewMode, setViewMode] = useState<'calendar' | 'grid'>('calendar');
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [addShiftOpened, { open: openAddShift, close: closeAddShift }] =
    useDisclosure(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [shiftForm, setShiftForm] = useState({
    employeeId: '',
    startTime: '',
    endTime: '',
    branchId: '',
    departmentId: '',
  });

  const weekStartStr = formatDate(weekStart);

  const { data: queryData, loading: isLoading } = useScheduleWeek(weekStartStr);
  const data = (queryData as unknown as { scheduleWeek?: ScheduleWeekData })?.scheduleWeek;

  const createShiftHook = useCreateShift();
  const createShiftLoading = createShiftHook.isPending;

  const navigateWeek = useCallback((direction: -1 | 1) => {
    setWeekStart((prev) => addDays(prev, direction * 7));
  }, []);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_: any, i) => {
      const date = addDays(weekStart, i);
      return {
        label: DAY_LABELS[i],
        date: formatDate(date),
        displayDate: date.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
      };
    });
  }, [weekStart]);

  const shiftsByDay = useMemo(() => {
    const map: Record<string, Shift[]> = {};
    for (const day of weekDays) {
      map[day.date] = [];
    }
    if (data?.shifts) {
      for (const shift of data.shifts) {
        const shiftDate = shift.startTime.split('T')[0] ?? '';
        if (shiftDate && map[shiftDate]) {
          map[shiftDate].push(shift);
        }
      }
    }
    return map;
  }, [data, weekDays]);

  const handleAddShift = async () => {
    await createShiftHook.mutateAsync({
      input: {
        employeeId: shiftForm.employeeId,
        startTime: shiftForm.startTime,
        endTime: shiftForm.endTime,
        branchId: shiftForm.branchId || undefined,
        departmentId: shiftForm.departmentId || undefined,
      },
    });
    setShiftForm({
      employeeId: '',
      startTime: '',
      endTime: '',
      branchId: '',
      departmentId: '',
    });
    closeAddShift();
  };

  const weekEndDate = addDays(weekStart, 6);
  const weekRangeLabel = `${weekStart.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })} - ${weekEndDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;

  return (
    <Stack gap="md">
      <PageHeader
        title="Schedule"
        subtitle={weekRangeLabel}
        actions={
          <Button
            icon={<IconPlus size={16} />}
            onClick={openAddShift}
          >
            Add Shift
          </Button>
        }
      />

      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <ActionIcon variant="subtle" onClick={() => navigateWeek(-1)}>
            <IconChevronLeft size={20} />
          </ActionIcon>
          <Text fw={500}>{weekRangeLabel}</Text>
          <ActionIcon variant="subtle" onClick={() => navigateWeek(1)}>
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>
        <SegmentedControl
          size="xs"
          value={viewMode}
          onChange={(v) => setViewMode(v as 'calendar' | 'grid')}
          data={[
            { value: 'calendar', label: <Group gap={4}><IconCalendar size={14} />Calendar</Group> },
            { value: 'grid', label: <Group gap={4}><IconTable size={14} />Grid</Group> },
          ]}
        />
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : viewMode === 'calendar' ? (
        <ShiftCalendar
          shifts={data?.shifts ?? []}
          initialDate={weekStartStr}
          onShiftClick={(shift) => setSelectedShift(shift as unknown as Shift)}
          onSlotSelect={(info) => {
            setShiftForm({
              employeeId: info.resourceId ?? '',
              startTime: info.start.toISOString().slice(0, 16),
              endTime: info.end.toISOString().slice(0, 16),
              branchId: '',
              departmentId: '',
            });
            openAddShift();
          }}
        />
      ) : (
        <SimpleGrid cols={7} spacing="xs">
          {weekDays.map((day) => (
            <Paper key={day.date} p="xs" withBorder>
              <Text fw={600} size="sm" mb={4}>
                {day.label}
              </Text>
              <Text size="xs" c="dimmed" mb="xs">
                {day.displayDate}
              </Text>
              <Stack gap={4}>
                {shiftsByDay[day.date]?.length === 0 && (
                  <Text size="xs" c="dimmed" ta="center" py="xs">
                    No shifts
                  </Text>
                )}
                {shiftsByDay[day.date]?.map((shift) => (
                  <Box
                    key={shift.id}
                    p={6}
                    style={{
                      borderRadius: 'var(--mantine-radius-sm)',
                      backgroundColor: `var(--mantine-color-${SHIFT_STATUS_COLORS[shift.status] || 'blue'}-light)`,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedShift(shift)}
                  >
                    <Text size="xs" fw={500} truncate>
                      {shift.employeeId}
                    </Text>
                    <Group gap={4} wrap="nowrap">
                      <IconClock size={10} />
                      <Text size="xs">
                        {formatTime(shift.startTime)} -{' '}
                        {formatTime(shift.endTime)}
                      </Text>
                    </Group>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* Add Shift Modal */}
      <Modal
        opened={addShiftOpened}
        onClose={closeAddShift}
        title="Add Shift"
        size="md"
      >
        <Stack gap="sm">
          <TextInput
            label="Employee ID"
            placeholder="Enter employee ID"
            required
            value={shiftForm.employeeId}
            onChange={(e) =>
              setShiftForm((f) => ({
                ...f,
                employeeId: e.currentTarget.value,
              }))
            }
          />
          <TextInput
            label="Start Time"
            placeholder="YYYY-MM-DDTHH:MM"
            type="datetime-local"
            required
            value={shiftForm.startTime}
            onChange={(e) =>
              setShiftForm((f) => ({
                ...f,
                startTime: e.currentTarget.value,
              }))
            }
          />
          <TextInput
            label="End Time"
            placeholder="YYYY-MM-DDTHH:MM"
            type="datetime-local"
            required
            value={shiftForm.endTime}
            onChange={(e) =>
              setShiftForm((f) => ({
                ...f,
                endTime: e.currentTarget.value,
              }))
            }
          />
          <TextInput
            label="Branch ID"
            placeholder="Optional"
            value={shiftForm.branchId}
            onChange={(e) =>
              setShiftForm((f) => ({
                ...f,
                branchId: e.currentTarget.value,
              }))
            }
          />
          <TextInput
            label="Department ID"
            placeholder="Optional"
            value={shiftForm.departmentId}
            onChange={(e) =>
              setShiftForm((f) => ({
                ...f,
                departmentId: e.currentTarget.value,
              }))
            }
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeAddShift}>
              Cancel
            </Button>
            <Button
              onClick={handleAddShift}
              loading={createShiftLoading}
              disabled={
                !shiftForm.employeeId ||
                !shiftForm.startTime ||
                !shiftForm.endTime
              }
            >
              Create Shift
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Shift Detail Modal */}
      <Modal
        opened={!!selectedShift}
        onClose={() => setSelectedShift(null)}
        title="Shift Details"
        size="sm"
      >
        {selectedShift && (
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Employee
              </Text>
              <Text size="sm" fw={500}>
                {selectedShift.employeeId}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Start
              </Text>
              <Text size="sm">
                {new Date(selectedShift.startTime).toLocaleString()}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                End
              </Text>
              <Text size="sm">
                {new Date(selectedShift.endTime).toLocaleString()}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Status
              </Text>
              <StatusBadge status={selectedShift.status} colorMap={SHIFT_STATUS_COLORS} />
            </Group>
            {selectedShift.branchId && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Branch
                </Text>
                <Text size="sm">{selectedShift.branchId}</Text>
              </Group>
            )}
            {selectedShift.departmentId && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Department
                </Text>
                <Text size="sm">{selectedShift.departmentId}</Text>
              </Group>
            )}
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => setSelectedShift(null)}
              >
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
